import { defineStore, Store } from "pinia";
import { DefaultGetParams, GetParams } from "@/interfaces/Types.definition";
import { getAllItemsAction, getItemAction } from "./common/actions";
import { SellingSession, ISellingSessionStats } from "@/interfaces/SellingSession";
import { useUserStore as userStore } from "./UserStore";
import { useEventStore as eventStore } from "./EventStore";
import { toast } from "vue3-toastify";
import axios from "@/tools/Axios/axios";
import { thermalCashOutType } from "./PrintStore";
import { GlobalHelpers } from "@/helpers/global.helper";
import { PaymentMethodEnum } from "@/interfaces/Order";

export interface SellingSessionState {
    currentSellingSession: SellingSession | null,
    sellingSessions: SellingSession[],
    stats: ISellingSessionStats
}

export const useSellingSessionStore = defineStore("SellingSessionStore", {
    state: (): SellingSessionState => {
        return {
            currentSellingSession: null,
            sellingSessions: [],
            stats: {
                totalPrice: 0,
                ordersTicketingCount: 0,
                ticketsCount: 0,
                ordersBuvetteCount: 0,
                paymentMethods: [],
                productsCount: 0,
                ticketsTotalPrice: 0,
                productsTotalPrice: 0
            }
        }
    },
    actions: {
        async fetch(sellingSessionId: string) {
            let params: GetParams = DefaultGetParams()
            params.populate = ['authorId']
            await getItemAction<SellingSession, Store<'SellingSessionStore'>>(`/ticket-offices/selling-sessions/${sellingSessionId}`, params, this, 'currentSellingSession')
        },
        async fetchAll(userRestricted: boolean = false) {

            let params: GetParams = DefaultGetParams()
            params.populate = ['authorId']
            params.filter = {
                eventId: eventStore().currentEvent?._id,
                status: 'opened,paused,closed'
            }
            if (userRestricted) {
                params.filter['authorId'] = userStore().me?._id
            }
            await getAllItemsAction<SellingSession, Store<'SellingSessionStore'>>(`/ticket-offices/selling-sessions/list`, params, this, 'sellingSessions')
        },
        async fetchStats(sellingSessionId: string = null) {
            let url = `/ticket-offices/selling-sessions/stats`
            try {
                const { data } = await axios.post(url, {
                    sellingSessionId,
                    eventId: eventStore().currentEvent._id
                }, {params: { eventId: eventStore().currentEvent._id }})

                this.stats = data
                return true
            } catch (error) {
                toast.error("Une erreur est survenue")
                toast.error(error.message)
                return false
            }
        },
        async downloadCashOut(sellingSessionId: string) {
            try {
                const response = await axios.post(
                    `/ticket-offices/selling-sessions/${sellingSessionId}/cash-out`,
                    {},
                    {
                        responseType: 'blob'
                    }
                )

                // Find the session to get session number
                const session = this.sellingSessions.find(s => s._id === sellingSessionId)
                const sessionNumber = session?.sessionNumber || 'session'
                const date = new Date().toISOString().split('T')[0]
                const filename = `sortie-caisse-${sessionNumber}-${date}.pdf`

                // Download PDF
                const { saveAs } = await import('file-saver')
                saveAs(response.data, filename)

                toast.success('Sortie de caisse téléchargée')
                return true
            } catch (error) {
                toast.error("Impossible de télécharger la sortie de caisse")
                toast.error(error.message)
                return false
            }
        },
        async downloadCashOutSummary() {
            try {
                const response = await axios.post(
                    `/ticket-offices/selling-sessions/cash-out`,
                    {
                        eventId: eventStore().currentEvent._id,
                        status: 'closed',
                        showSessionsDetails: true
                    },
                    {
                        responseType: 'blob'
                    }
                )

                const date = new Date().toISOString().split('T')[0]
                const filename = `synthese-sorties-caisse-${date}.pdf`

                // Download PDF
                const { saveAs } = await import('file-saver')
                saveAs(response.data, filename)

                toast.success('Synthèse des sorties de caisse téléchargée')
                return true
            } catch (error) {
                toast.error("Impossible de télécharger la synthèse des sorties de caisse")
                toast.error(error.message)
                return false
            }
        },
        prepareCashOutPrintData(session: SellingSession): thermalCashOutType {
            const stats = this.stats
            const event = eventStore().currentEvent

            // Helper pour formater les prix pour l'impression thermique (sans séparateur de milliers)
            const formatPriceForThermal = (amountInCents: number): string => {
                const euros = amountInCents / 100
                // Format sans séparateur de milliers pour éviter les problèmes d'encodage Cp850
                return euros.toFixed(2).replace('.', ',') + ' EUR'
            }

            // Préparer les types de tickets depuis les stats
            const ticketTypesFormatted = stats.ticketTypes && stats.ticketTypes.length > 0
                ? stats.ticketTypes.map(tt => ({
                    label: tt.ticketTypeName,
                    quantity: tt.ticketCount,
                    amount: formatPriceForThermal(tt.total)
                }))
                : [{
                    label: 'Billets vendus',
                    quantity: stats.ticketsCount,
                    amount: formatPriceForThermal(stats.ticketsTotalPrice)
                }]

            return {
                sessionNumber: session.sessionNumber,
                organisation: event.organisationId?.name || 'N/A',
                event: event.name,
                operator: `${session.authorId?.firstName} ${session.authorId?.lastName}`,
                openedAt: GlobalHelpers.formatDate(session.createdAt),
                closedAt: session.closedAt ? GlobalHelpers.formatDate(session.closedAt) : 'N/A',
                sales: {
                    orders: stats.ordersTicketingCount,
                    tickets: stats.ticketsCount,
                    amount: formatPriceForThermal(stats.ticketsTotalPrice)
                },
                ticketTypes: ticketTypesFormatted,
                paymentMethods: stats.paymentMethods.map(pm => ({
                    label: pm.customMethod ? pm.customMethod.label : (PaymentMethodEnum[pm.method] || pm.method),
                    amount: formatPriceForThermal(pm.total)
                })),
                totals: {
                    receipts: formatPriceForThermal(stats.totalPrice),
                    refunds: formatPriceForThermal(0),
                    balance: formatPriceForThermal(stats.totalPrice)
                }
            }
        }
    }
})